import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
// Utils
import RealtimeUtil from '../utils/RealtimeUtil';
import { showLoadingToast, showToast } from '../utils/ToastsUtil';
import { saveElementsToSnapshot } from '../utils/offline/OfflineRead';
import { canUseFieldWrite } from '../utils/offline/FieldWrite';
import { addElementHistory, createElement, createElements, removeElements, updateElement, updateElements } from '../utils/offline/ElementWrites';
import i18n from '../locales/i18n';
import { isOnline } from '../utils/offline/Connectivity';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class FurnituresService {
    static addFurniture(furniture, action) {
        furniture.id = uuidv4();
        furniture.properties.furnitureId = furniture.id;
        // Pas de this : méthode passée détachée par ProjectMap
        if (canUseFieldWrite()) return FurnituresService.addFurnitureThroughOutbox(furniture, action);
        const promise = Axios.post(BACKEND_ENDPOINT + 'furnitures/' + RealtimeUtil.getConnectionId(), furniture, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            furniture.properties = response.data.properties;
            console.log('#fetchId', furniture.fetchId, response.data.fetchId);
            furniture.fetchId = response.data.fetchId;
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'furniture_' + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = 'furniture_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'furniture_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static addFurnitureThroughOutbox(furniture, action) {
        const promise = createElement('Mobilier', furniture).then(response => {
            if (!response.isLocal) {
                furniture.properties = response.data.properties;
                furniture.fetchId = response.data.fetchId;
            }
            return response;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'furniture_' + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = 'furniture_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'furniture_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static addFurnitures(furnitures, action) {
        furnitures.forEach(f => {
            f.id = uuidv4();
            f.properties.furnitureId = f.id;
        });
        if (canUseFieldWrite()) return FurnituresService.addFurnituresThroughOutbox(furnitures, action);
        const promise = Axios.post(BACKEND_ENDPOINT + 'furnitures/bulk/' + RealtimeUtil.getConnectionId(), furnitures, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            response.data.forEach(f => {
                let furniture = furnitures.find(x => x.id === f.id);
                furniture.properties = f.properties;
                furniture.fetchId = f.fetchId;
                console.log('#fetchId', furniture.fetchId, f.fetchId);
            });
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefix = furnitures.length > 1 ? 'furnitures_' : 'furniture_';
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise, furnitures.length);
        return promise;
    }

    // Comme la réponse de l'API, data contient les mobiliers, complétés par le serveur s'il a répondu à temps
    static addFurnituresThroughOutbox(furnitures, action) {
        const promise = createElements('Mobilier', furnitures).then(results => {
            const furnituresById = new Map(furnitures.map(furniture => [furniture.id, furniture]));
            results.filter(result => !result.isLocal).flatMap(result => result.data).forEach(serverFurniture => {
                const furniture = furnituresById.get(serverFurniture.id);
                if (!furniture) return;
                furniture.properties = serverFurniture.properties;
                furniture.fetchId = serverFurniture.fetchId;
            });
            return { data: furnitures, isLocal: results.some(result => result.isLocal) };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const prefix = furnitures.length > 1 ? 'furnitures_' : 'furniture_';
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise, furnitures.length);
        return promise;
    }

    static addFurnitureHistory(furniture, date) {
        if (canUseFieldWrite()) return addElementHistory('Mobilier', furniture, date).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : 'history_added');
            return response.isLocal ? undefined : response.data;
        }, () => {
            showToast('history_addition_failed');
            return;
        });
        return Axios.post(BACKEND_ENDPOINT + 'furnitures/history/?date=' + date.toISOString(), furniture, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('history_added');
            return response.data;
        }, () => {
            if (isOnline()) showToast('history_addition_failed');
            return;
        });
    }

    // saveToSnapshot faux : la carte écrit elle-même la copie, par priorité (chargement par cache Redis)
    static getFurnitures(projectId, data, { saveToSnapshot = true } = {}) {
        return Axios.post((!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url)) && (data?.page || data?.page === 0)
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT) + 'furnitures/projects/' + projectId, data, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (saveToSnapshot) saveElementsToSnapshot(projectId, 'Mobilier', response.data?.furnitures);
            return response.data;
        }, () => {
            if (isOnline() && !data?.cacheKey && !data?.cursor) showToast('furnitures_loading_failed');
            return;
        });
    }

    static removeFurnitures(furnitures) {
        if (canUseFieldWrite()) return FurnituresService.removeFurnituresThroughOutbox(furnitures);
        const promise = Axios.delete(BACKEND_ENDPOINT + 'furnitures/' + RealtimeUtil.getConnectionId(), {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: furnitures
        }).then(response => {
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefixe = furnitures.length > 1 ? 'furnitures' : 'furniture';
        showLoadingToast(prefixe + '_deleting', prefixe + '_deleted', prefixe + '_deletion_failed', promise);
        return promise;
    }

    static removeFurnituresThroughOutbox(furnitures) {
        const promise = removeElements('Mobilier', furnitures).catch(error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });
        const prefix = furnitures.length > 1 ? 'furnitures' : 'furniture';
        showLoadingToast(prefix + '_deleting', prefix + '_deleted', prefix + '_deletion_failed', promise);
        return promise;
    }

    static updateFurniture(id, furniture, history, action, previousFeature = null) {
        if (canUseFieldWrite()) return FurnituresService.updateFurnitureThroughOutbox(id, furniture, history, action, previousFeature);
        const promise = Axios.put(BACKEND_ENDPOINT + 'furnitures/?connectionId=' + RealtimeUtil.getConnectionId() + '&id=' + id + '&history=' + history, furniture, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            furniture.properties = { ...furniture.properties, ...response.data.furniture.properties };
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'furniture_' + action;
        const successId = 'furniture_' + action.replace('ing', 'ed');
        const errorId = 'furniture_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static updateFurnitureThroughOutbox(id, furniture, history, action, previousFeature) {
        const promise = updateElement('Mobilier', id, furniture, { history }, previousFeature).then(response => {
            if (!response.isLocal) furniture.properties = { ...furniture.properties, ...response.data.furniture.properties };
            return response;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'furniture_' + action;
        const successId = 'furniture_' + action.replace('ing', 'ed');
        const errorId = 'furniture_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static bulkUpdateFurnitures(furnitures, action, previousFeatures = null) {
        if (canUseFieldWrite()) return FurnituresService.bulkUpdateFurnituresThroughOutbox(furnitures, action, previousFeatures);
        const promise = Axios.put(BACKEND_ENDPOINT + 'furnitures/bulk/' + RealtimeUtil.getConnectionId(), furnitures, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (Array.isArray(response?.data)) {
                response.data.forEach(feature => {
                    let furniture = furnitures.find(furniture => furniture.id === feature.id);
                    if (furniture) furniture.properties = { ...furniture.properties, ...feature.properties };
                });
            }
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'furnitures_' + action;
        const successId = 'furnitures_' + action.replace('ing', 'ed');
        const errorId = 'furnitures_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    // Réponse locale si un paquet n'est pas accepté dans le délai ; data contient les éléments, complétés par le serveur sinon
    static bulkUpdateFurnituresThroughOutbox(furnitures, action, previousFeatures) {
        const promise = updateElements('Mobilier', furnitures, {}, previousFeatures).then(results => {
            const byId = new Map(furnitures.map(feature => [feature.id, feature]));
            results.filter(result => !result.isLocal).flatMap(result => Array.isArray(result.data) ? result.data : []).forEach(serverFeature => {
                const feature = byId.get(serverFeature.id);
                if (feature) feature.properties = { ...feature.properties, ...serverFeature.properties };
            });
            return { data: furnitures, isLocal: results.some(result => result.isLocal) };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'furnitures_' + action;
        const successId = 'furnitures_' + action.replace('ing', 'ed');
        const errorId = 'furnitures_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static getFurnitureHistory(id) {
        return Axios.get(BACKEND_ENDPOINT + 'furnitures/history/?id=' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('element_history_loading_failed');
            return;
        });
    }

    static getConditions() {
        return Axios.get(BACKEND_ENDPOINT + 'furnitures/conditions', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('conditions_loading_failed');
            return;
        });
    }

    static getFurnitureTypes() {
        return Axios.get(BACKEND_ENDPOINT + 'furnitures/types', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('furniture_types_loading_failed');
            return;
        });
    }

    static exportFurnituresFromProjectAsExcel(name, id, elementIds = [], projection) {
        return Axios.post(BACKEND_ENDPOINT + 'furnitures/exportXLSX/' + id, { elementIds, projection }, {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id} - ${name} (${i18n.t("Mobilier urbain").toLowerCase()}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
            return true;
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }

    static exportFurnitureAsPDF(furniture, image, addPrices, photosId, colorless, toastCustomTexts, historyId = null) {
        return Axios.post(BACKEND_ENDPOINT + 'furnitures/exportPDF/' + furniture.id, { image, addPrices, photosId, colorless, historyId }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            let filename = furniture.properties.projectReference ? `${i18n.t("Fiche mobilier")}_${furniture.properties.projectReference}` : i18n.t("Fiche mobilier");
            if (furniture.properties.customReference) filename += '_' + furniture.properties.customReference;
            filename += '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
        });
    }

    static exportFurnituresAsPDF(projectId, elementIds, { projection, colorless = false, addPhotos = true } = {}) {
        return Axios.post(BACKEND_ENDPOINT + 'furnitures/exportAllPDF/' + projectId, { elementIds, projection, colorless, addPhotos }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response;
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
            return;
        });
    }

    static exportAllBacklogsAsPDF(projectId, grouping) {
        return Axios.get(BACKEND_ENDPOINT + 'furnitures/exportAllBacklogsPDF/' + grouping + '/' + projectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
            }
        }).then(response => {
            return response;
        }, () => {
            if (isOnline()) showToast('backlogs_pdfs_export_failed');
        });
    }

    static exportFurnituresAsSHP(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'furnitures/exportSHP/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Mobilier urbain").toLowerCase()}).zip`);
                document.getElementById('modal-content').appendChild(link);
                link.click();
                document.getElementById('modal-content').removeChild(link);
                URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_shp_export_failed');
        });
    }

    static exportFurnituresAsGPKG(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'furnitures/exportGPKG/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const blob = new Blob([response.data], { type: 'application/geopackage+sqlite3' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Mobilier urbain").toLowerCase()}).gpkg`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_gpkg_export_failed');
        });
    }
}
